import { ApiValidation, ApiValidationErrorType } from "../model";
import { Objects } from "../util";

/**
 * Helper for custom validation methods.
 */
export class ValidationHelper {

  /** The validation object that is used for adding errors warnings etc. */
  public validation: ApiValidation | undefined = undefined;

  constructor(validation: ApiValidation) {
    if (!validation) {
      throw new Error("Validation object is null.");
    }
    this.validation = validation;
    (validation as any).errors = validation.errors || [];
  }

  /**
   * Add a warning to the validations object
   * @param message Required warning message
   * @param code Optional unique code for the validation error
   * @param field Full path to Name of the property / field (Member name in JSON).
   */
  public warn(message: string, code: string | undefined = undefined, field: string | undefined = undefined): void {
    this.validation ??= {};
    this.validation.errors?.push({
      msg: message,
      type: ApiValidationErrorType.Warning,
      code,
      key: field,
    });
    this.recalc();
  }

  /** Add a error to the validations object */
  public error(message: string, code: string | undefined = undefined, field: string | undefined = undefined): void {
    this.validation ??= {};
    this.validation.errors?.push({
      msg: message,
      type: ApiValidationErrorType.General,
      code,
      key: field,
    });
    this.recalc();
  }

  /** Adds a missing required field to validations object */
  public missing(field: string, message: string | undefined = undefined): void {
    this.validation ??= {};
    this.validation.errors?.push({
      msg: message || "Kenttä puuttuu", // TODO: Add language versioned message.
      type: ApiValidationErrorType.Required,
      code: "missing", // TODO: Check what is the best practice for this.
      key: field,
    });
    this.recalc();
  }

  /**
   * Checks that the required item is not null / empty.
   * If it is adds a required field missing error.
   */
   public checkNotEmpty(root: any, path: string, message: string | undefined = undefined): boolean {
    const value = Objects.getProperty(root, path);
    if (!value && value !== 0) {
      this.missing(path, message);
      return false;
    }
    return true;
  }

  /**
   * Clears any existing validation errors
   * @param code If specified clears only errors with specified code. May be a string or regexp.
   */
  public clear(code: RegExp | string | undefined = undefined): void {
    let newValidations: any[] | undefined = [];
    this.validation ??= {};
    if (code) {
      if ((code as RegExp).test) {
        newValidations = this.validation.errors?.filter((x) => x.code &&!(code as RegExp).test(x.code));
      } else {
        newValidations = this.validation.errors?.filter((x) => x.code != code);
      }
    }
    (this.validation as any).errors = newValidations;
    this.recalc();
  }

  /**
   * Merges the errors of another validation error to this validation.
   * @param validation Another api validation object with potential errors.
   * May also be null or with null error array without raising errors.
   */
  public merge(validation: ApiValidation): void {
    if (validation?.errors && validation?.errors?.length > 0) {
      this.validation?.errors?.push(...validation.errors);
      this.recalc();
    }
  }

  /** Recalculates the read-only helper properties based on the errors list. */
  public recalc(): void {
    const result = (this.validation as any);
    result.errors = this.validation?.errors || [];
    result.hasAllRequiredFields = !this.validation?.errors?.find((x) => x.type == ApiValidationErrorType.Required);
    result.isValid = !this.validation?.errors?.find((x) => x.type != ApiValidationErrorType.Warning);

  }

}